import React, { useEffect, useRef, useState } from 'react';
import Chatbot, { MessagesContext, PathsContext } from 'react-chatbotify';
import { ChatBotFooter } from './ChatBotFooter';
import { chatWindowStyle } from './chatBotStyleObjects';
import { InferenceResponse, InputInfo, ModelDefinitionDto } from '../../models/APIModels';
import { InferenceStatus } from '../../enums';
import inferenceHelper from '../ModelViewer/inferenceHelper';
import { FormSection, InputProps } from '../../models/type';
import { getInputs } from '../../Utils/getFormInputs';
import Form from '../Form/Form';
import { Button, Card, Grid } from '@mui/material';
import { uploadFile } from '../../common/Upload';
import { ApiHelper } from '../../common/ApiHelper';
import Message from './Message';

import ChatbotHeader from './ChatbotHeader';
import clientUtils from '../../Utils/clientUtils';

interface Iprop {
  model?: ModelDefinitionDto;
  setTotalChatbotCost?: any;
  embedInputQueryParams?: any;
  updateLog?: any;
  callBackToIframe?:() => void;
}
const ChatBotInterface = ({ model, setTotalChatbotCost, embedInputQueryParams = {}, updateLog = () => {}, callBackToIframe }: Iprop) => {
  // ▶️ Variables .....
  const [paths, setPaths] = React.useState(['start']);
  const [messages, setMessages] = useState<any>([]);
  const [contextID, setContextID] = useState();
  const [showFileAttachmentIcon, setShowFileAttachmentIcon] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [dynamicForm, setDynamicForm] = useState({});
  const [options, setOptions] = useState([]);
  const [message, setMessage] = useState('');

  const formComponentRef = useRef();
  const inputFormFileDataRef = useRef({});
  const dynamicFormDataRef = useRef(null);
  const [queryParams, setQueryParams] = useState(embedInputQueryParams);
  const fileUploadInfo = useRef({ isFileUploadInProgress: false, inputFile: null });
  const requestIdRef = useRef(null);
  const chatbotRef = useRef(null);
  const autoInvokeTimeoutRef = useRef(null);

  // ▶️ Functions/methods .....
  const handleUpload = async (params) => {
    // console.log('handlefile', { params });
    const files = params.files;
    if (files.length === 0) {
      return;
    }
    const file = files[0];
    fileUploadInfo.current.isFileUploadInProgress = true;
    fileUploadInfo.current.inputFile = file;
    if (!requestIdRef.current) {
      const inferenceResponse = await inferenceHelper.initializeInference(model);
      if (inferenceResponse) {
        requestIdRef.current = inferenceResponse.requestId;
        setContextID(inferenceResponse.contextId);
      }
    }
    if (requestIdRef && requestIdRef.current) {
      const reqId = requestIdRef.current;
      const { fileName, uploadLink } = await inferenceHelper.getUploadLinkForInference(reqId, file.name);
      if (uploadLink) {
        await uploadFile(file, uploadLink);
        fileUploadInfo.current.isFileUploadInProgress = false;
        setPaths((prev) => [...prev, 'userInput']);
        await onPredictRun({ userInput: '' });
      }
    }
  };

  const onFileChange = async (type: string, file: File, name: string) => {
    inputFormFileDataRef.current[name] = file;
  };

  const onPredict = () => {
    if (formComponentRef.current) {
      formComponentRef.current.onPredictSubmit();
    }
  };
  const onSubmitInput = async (data: unknown) => {
    if (!data) {
      setPaths((prev) => [...prev, 'userInput']);
      return;
    }
    dynamicFormDataRef.current = data;
    setPaths((prev) => [...prev, 'submitForm']);
  };

  const onFormChange = (data: unknown) => {
    dynamicFormDataRef.current = data;
  };

  const chatbotFlow = {
    start: {
      message: model?.interfaceDisplayOptions.chatbotStartMessage
        ? model?.interfaceDisplayOptions.chatbotStartMessage
        : 'How can I help you ?',
      function: async (params) => onPredictRun(params),
    },
    showMessage: {
      message: (params) => {
        // console.log('params', params);
      },
      render: (params) => {
        return <Message text={message} />;
      },
      function: async (params) => onPredictRun(params),
    },
    userInput: {
      function: async (params) => {
        if (!fileUploadInfo.current.isFileUploadInProgress) {
          await onPredictRun(params);
        }
      },
      path: 'userInput',
      file: async (params) => await handleUpload(params),
      chatDisabled: fileUploadInfo.current.isFileUploadInProgress,
    },
    dynamicForm: {
      messages: async (params) => {},
      render: (params) => {
        // console.log('params', params);
        return (
          <>
            <Card sx={{ margin: '1rem' }}>
              <Grid container item sx={{ padding: '1rem' }}>
                <Grid item xs={12}>
                  {dynamicForm && (
                    <Form
                      {...dynamicForm}
                      initialValues={dynamicForm?.initialValues}
                      titleForm="Inputs"
                      onSubmit={onSubmitInput}
                      labelButtonSubmit="Predict"
                      onFormChange={onFormChange}
                      showButtons={false}
                      ref={formComponentRef}
                      onInputActionFun={onFileChange}
                      uploadingInProgress={false}
                      showTitle={false}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Button size="small" sx={{ width: '25%', height: '35px' }} variant="contained" onClick={onPredict}>
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </>
        );
      },
      chatDisabled: true,
    },
    submitForm: {
      message: async (params) => {
        // await params.injectMessage("I am an injected message!");
        await onPredictRun(params);
      },
    },
    show_options: {
      options: options,
      path: 'processOptions',
    },
    processOptions: {
      transition: { duration: 0 },
      path: async (params) => {
        await onPredictRun(params);
      },
    },
    autoInvoke: {
      message: async (params) => {
        params.userInput = '';
        await onPredictRun(params);
      },
      function: async (params) => onPredictRun(params),
    },
  };

  const dictionaryToArrayOfObjects = (dict) => {
    return Object.entries(dict).map(([key, value]) => ({ name: key, inputType: value }));
  };

  const generateOutcomeDataText = async (responseData: InferenceResponse) => {
    // Initialize an array to store outcome data objects
    // console.log('responseData', responseData);
    const outcomeData: any = [];
    if (responseData && responseData.status === InferenceStatus.Completed) {
      const outcomes = responseData.outcomes;
      const optionsOutcome = outcomes['Options'] ? outcomes['Options'] : [];
      const autoInvokeIn = outcomes['AutoInvokeIn'] ? outcomes['AutoInvokeIn'] : null;
      const dynamicFormDefinitions = outcomes['DynamicFormDefinitions'] ? outcomes['DynamicFormDefinitions'] : null;

      if (outcomes && outcomes.hasOwnProperty('OutputMessage')) {
        const outputMessage = outcomes['OutputMessage'];
        if (outputMessage.length === 1) {
          if (outcomes && outcomes.hasOwnProperty('DynamicFormDefinitions') && optionsOutcome.length === 0) {
            msgSender(outputMessage[0].value);
          } else {
            setMessage(() => outputMessage[0].value);
            setPaths((prev) => ['showMessage']);
          }
        } else if (outputMessage.length > 1) {
          const msgs = outputMessage.map((o) => {
            if (!clientUtils.isEmpty(o.value)) {
              msgSender(o.value);
            }
          });
        }
      }
      if (autoInvokeIn && autoInvokeIn.length > 0 && autoInvokeIn[0].value > 0) {
        const ms = autoInvokeIn[0].value * 1000;
        startAutoInvoke(ms);
        return;
      }
      if ((autoInvokeIn && autoInvokeIn.length > 0 && autoInvokeIn[0].value === 0) || !autoInvokeIn) {
        clearAutoInvokeRef();
      }
      if (outcomes && outcomes.hasOwnProperty('DynamicFormDefinitions') && optionsOutcome.length === 0) {
        const form = outcomes['DynamicFormDefinitions'];
        if (form && form.length > 0) {
          let formInputs = dictionaryToArrayOfObjects(form[0].value);
          formInputs = formInputs.filter((input) => input.inputType !== 'File');
          const inputs = inferenceHelper.generateInputs(formInputs, '');
          const forms: { [K in FormSection]: InputProps[] } = {
            Input: inputs,
            Outcomes: [],
          };
          setDynamicForm(getInputs<InputInfo>('Input', forms));
          setPaths((prev) => ['dynamicForm']);
        }
      }
      if (optionsOutcome && optionsOutcome.length > 0) {
        const opts = optionsOutcome.map((o) => o.value);
        setOptions(() => opts);
        setPaths((prev) => [...prev, 'show_options']);
      }
      if (optionsOutcome.length === 0 && !dynamicFormDefinitions) {
        setOptions([]);
      }
    } else if (responseData && responseData.status === InferenceStatus.Error) {
      setHasError(true);
      return [{ error: { isError: true, errMsg: 'No outcome values available' } }];
    }

    // If outcomeData array is empty, return an error object
    if (outcomeData.length === 0) {
      return [{ error: { isError: true, errMsg: "I'm having trouble connecting to the network!" } }];
    }

    return outcomeData;
  };

  function msgSender(content: any) {
    setMessages((prev) => [
      ...prev,
      {
        content: content,
        sender: 'bot',
        type: 'string',
      },
    ]);
  }
  const getInputsPayload = (userInput) => {
    const inputsPayload = {
      InputMessage: userInput,
      InputFile: fileUploadInfo.current.inputFile ? fileUploadInfo.current.inputFile.name : null,
      EmbedInputs: queryParams,
      DynamicFormResults: dynamicFormDataRef.current,
    };
    return inputsPayload;
  };
  const onPredictRun = async (params: any) => {
    // console.log('chatbot user input', { params });
    if (fileUploadInfo.current.isFileUploadInProgress) {
      return;
    }
    try {
      // clearMessages(); // Assuming this is not needed
      const inputName = model?.aiEngineConfig.inputs[0]?.name;
      if (!inputName) {
        throw new Error('Input name not found.');
      }
      const dataInputJson = getInputsPayload(params.userInput);
      const desiredOutcomes = model?.aiEngineConfig?.outcomes?.map((o) => o.name) || [];
      const payload = {
        RequestId: requestIdRef?.current,
        InputValues: dataInputJson,
        DesiredOutcomes: desiredOutcomes,
        contextId: contextID ? contextID : null,
      };
      const response = await ApiHelper.Predict(payload, model.modelId);
      if (response && response.data && response.status === 200) {
        updateLog(response.data.logMessages, response.data.errorMessages);
        setContextID(response.data.contextId);
        setTotalChatbotCost && setTotalChatbotCost((pv) => pv + response.data.computeCost);
        await generateOutcomeDataText(response.data);
        if (fileUploadInfo.current.inputFile) {
          fileUploadInfo.current.inputFile = null;
        }
      } else if (response && response.response.data && response.response.status === 401) {
        msgSender('Please login to continue!');
      }
    } catch (error) {
      console.error({ error });
      setHasError(true);
      msgSender("I'm having trouble connecting to the network!");
    } finally {
      requestIdRef.current = null;
    }
  };

  const onExit = () => {
    setContextID(null);
    setMessages(() => []);
    clearAutoInvokeRef();
    setPaths((prev) => [...prev, 'start']);
    if (callBackToIframe) {
      callBackToIframe();
    }
    
  };

  const startAutoInvoke = (ms) => {
    autoInvokeTimeoutRef.current = setTimeout(() => {
      setMessages((prev) => [...prev, '']);
      setPaths((prev) => [...prev, 'autoInvoke']);
    }, ms);
  };

  // Function to clear the timeout
  const clearAutoInvokeRef = () => {
    clearTimeout(autoInvokeTimeoutRef.current);
  };

  const chatBotoptions = {
    theme: { embedded: true, primaryColor: '#fff', secondaryColor: '#fff' },
    chatWindowStyle: chatWindowStyle,
    userBubble: { dangerouslySetInnerHtml: true },
    botBubble: { simStream: true, dangerouslySetInnerHtml: true },

    // remember to enable custom paths under the advance section
    advance: {
      useCustomPaths: true,
      useCustomMessages: true,
    },
    chatHistory: {
      disabled: true,
      maxEntries: 10,
    },
    header: {
      title: <ChatbotHeader title={model?.modelName} onExit={onExit} />,
    },
    fileAttachment: {
      disabled: showFileAttachmentIcon,
      multiple: false,
      accept: '',
      // icon: fileAttachmentIcon,
    },
    emoji: {
      disabled: true,
    },
    chatInput: {
      allowNewline: true,
    },
    footer: { text: <ChatBotFooter /> },
  };

  useEffect(() => {
    if (model) {
      const inputs = model.aiEngineConfig?.inputs;
      if (inputs && inputs.length > 0) {
        const isFileInput = inputs.find((input) => input.inputType === 'File');
        if (isFileInput) {
          setShowFileAttachmentIcon(() => true);
        }
      }
    }
  }, [model]);

  useEffect(() => {
    // console.log('paths updated', paths);
  }, [paths]);
  useEffect(() => {}, []);

  return (
    <div id="unity-predict-chat-bot" ref={chatbotRef}>
      <MessagesContext.Provider value={{ messages: messages, setMessages: setMessages }}>
        <PathsContext.Provider value={{ paths: paths, setPaths: setPaths }}>
          <Chatbot options={chatBotoptions} flow={chatbotFlow} />
        </PathsContext.Provider>
      </MessagesContext.Provider>
    </div>
  );
};

export default ChatBotInterface;
